import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useSystemsContext } from "../../../context/SystemsContext";
import {
  SystemAlias,
  SystemAliasInput,
  useAliasApi,
} from "../../../api/useAliasApi";
import AliasDialog from "./AliasDialog";

type DialogState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; alias: SystemAlias };

const SystemAliasesForm: React.FC = () => {
  const { formData } = useSystemsContext();
  const { listAliasesApi, createAliasApi, updateAliasApi, deleteAliasApi } = useAliasApi();
  const systemCode = (formData?.systemCode || "").toUpperCase();

  const [aliases, setAliases] = useState<SystemAlias[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogState, setDialogState] = useState<DialogState>({ mode: "closed" });
  const [deletingAlias, setDeletingAlias] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!systemCode) {
      setAliases([]);
      return;
    }
    setLoading(true);
    try {
      const list = await listAliasesApi(systemCode);
      setAliases(list);
    } catch (err) {
      console.error("Failed to load aliases:", err);
      setAliases([]);
    } finally {
      setLoading(false);
    }
  }, [systemCode, listAliasesApi]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSubmit = async (input: SystemAliasInput) => {
    if (dialogState.mode === "create") {
      await createAliasApi(input);
    } else if (dialogState.mode === "edit") {
      await updateAliasApi(input.alias, {
        systemCode: input.systemCode,
        imageSet: input.imageSet,
        commonName: input.commonName,
      });
    }
    await refresh();
  };

  const handleDelete = async (alias: string) => {
    if (!window.confirm(`Delete alias "${alias}"? Customer URLs using this alias will stop working.`)) {
      return;
    }
    setDeletingAlias(alias);
    try {
      await deleteAliasApi(alias);
      await refresh();
    } catch (err) {
      console.error("Failed to delete alias:", err);
    } finally {
      setDeletingAlias(null);
    }
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }} data-cy="alias-section">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            iFrame URL Aliases
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Customer-friendly path segments for /iframe URLs. Each alias maps to{" "}
            <Box component="span" sx={{ fontFamily: "monospace" }}>{systemCode || "this system"}</Box>{" "}
            and optionally locks the displayed image set.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogState({ mode: "create" })}
          disabled={!systemCode}
          data-cy="alias-add-btn"
        >
          Add Alias
        </Button>
      </Stack>

      {loading ? (
        <Box
          sx={{ display: "flex", justifyContent: "center", py: 4 }}
          data-cy="alias-loading"
        >
          <CircularProgress size={28} />
        </Box>
      ) : aliases.length === 0 ? (
        <Box
          sx={{
            py: 4,
            textAlign: "center",
            color: "text.secondary",
            border: (theme) => `1px dashed ${theme.palette.divider}`,
            borderRadius: 1,
          }}
          data-cy="alias-empty-state"
        >
          <Typography variant="body2">
            No aliases yet. Add one to start serving customer URLs like{" "}
            <Box component="span" sx={{ fontFamily: "monospace" }}>/iframe/devunit/&#123;assetId&#125;</Box>.
          </Typography>
        </Box>
      ) : (
        <TableContainer data-cy="alias-table">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Alias</TableCell>
                <TableCell>Image Set</TableCell>
                <TableCell>Common Name</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aliases.map((row) => (
                <TableRow key={row.alias} hover data-cy={`alias-row-${row.alias}`}>
                  <TableCell sx={{ fontFamily: "monospace" }}>{row.alias}</TableCell>
                  <TableCell data-cy={`alias-image-set-${row.alias}`}>
                    {row.imageSet ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {row.imageSet.split("-").map((token) => (
                          <Chip key={token} label={token} size="small" />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">All images</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.commonName || (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => setDialogState({ mode: "edit", alias: row })}
                        data-cy={`alias-edit-${row.alias}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deletingAlias === row.alias}
                        onClick={() => handleDelete(row.alias)}
                        data-cy={`alias-delete-${row.alias}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AliasDialog
        open={dialogState.mode !== "closed"}
        mode={dialogState.mode === "edit" ? "edit" : "create"}
        systemCode={systemCode}
        existing={dialogState.mode === "edit" ? dialogState.alias : null}
        onClose={() => setDialogState({ mode: "closed" })}
        onSubmit={handleSubmit}
      />
    </Paper>
  );
};

export default SystemAliasesForm;
